import React, { type FormEvent } from 'react';
import {
  Badge,
  Box,
  Column,
  Columns,
  Dropdown,
  Stack,
  Text,
  Strong,
} from 'braid-design-system';
import type {
  IStandardFilterAnswer,
  IFilterQuestion,
} from 'src/stores/JobApplicationStore/FiltersStore/FiltersStore';
import { observer } from 'mobx-react';
import { BarGraph, type BarGraphData } from './BarGraph';
import translations from './.vocab/';
import { useTranslations } from '@vocab/react';

interface RangeFilterProps {
  question: IFilterQuestion;
  maxHeight?: number;
  onSelect: () => void;
}

export const RangeFilter = observer(
  ({ question, onSelect }: RangeFilterProps) => {
    const { t } = useTranslations(translations);
    const { answers, selectOnlyAnswersInRange } = question;

    if (answers.length === 0) {
      return null;
    }

    const barGraphItems: BarGraphData[] = answers.map((answer) => ({
      id: answer.answerInfo.globalIdOrId,
      label: `${answer.answerInfo.text} (${answer.totalCount} applications)`,
      isSelected:
        answer.answerInfo.order >= question.minOrderSelected &&
        answer.answerInfo.order <= question.maxOrderSelected,
      value: answer.totalCount,
    }));

    const handleMinDropdownChange = (event: FormEvent<HTMLSelectElement>) => {
      selectOnlyAnswersInRange(
        Number(event.currentTarget.value),
        question.maxOrderSelected,
      );
      onSelect();
    };
    const handleMaxDropdownChange = (event: FormEvent<HTMLSelectElement>) => {
      selectOnlyAnswersInRange(
        question.minOrderSelected,
        Number(event.currentTarget.value),
      );
      onSelect();
    };

    const minOptions: IStandardFilterAnswer[] = answers.filter(
      (a) => a.answerInfo.order <= question.maxOrderSelected,
    );

    const maxOptions: IStandardFilterAnswer[] = answers.filter(
      (a) => a.answerInfo.order >= question.minOrderSelected,
    );

    const renderOptions = (answerOptions: IStandardFilterAnswer[]) =>
      answerOptions.map((answer) => (
        <option
          key={answer.answerInfo.globalIdOrId}
          value={answer.answerInfo.order}
          disabled={answer.disabled}
        >
          {answer.answerInfo.text}
          {answer.answerInfo.isPreferred && '*'} ({answer.count})
        </option>
      ));

    return (
      <Stack space="medium">
        {question.showPreferredAnswersMessage && (
          <Text tone="secondary" size="xsmall">
            {t('Your preferred range has been selected', {
              Strong: (children) => (
                <Strong key={`${children}`}>{children}</Strong>
              ),
            })}
          </Text>
        )}
        {!question.disabled ? <BarGraph items={barGraphItems} /> : null}
        <Columns space="medium" alignY="bottom">
          <Column>
            <Dropdown
              label={t('Min')}
              onChange={handleMinDropdownChange}
              value={question.minOrderSelected}
              id={`min-dropdown-${question.questionInfo.globalIdOrId}`}
            >
              {renderOptions(minOptions)}
            </Dropdown>
          </Column>
          <Column>
            <Dropdown
              label={t('Max')}
              onChange={handleMaxDropdownChange}
              value={question.maxOrderSelected}
              id={`max-dropdown-${question.questionInfo.globalIdOrId}`}
            >
              {renderOptions(maxOptions)}
            </Dropdown>
          </Column>
          <Column width="content">
            <Box paddingBottom="small">
              <Badge
                tone="neutral"
                title={`filter-total-count-${question.questionInfo.globalIdOrId}`}
              >{`${question.selectedAnswersCount}`}</Badge>
            </Box>
          </Column>
        </Columns>
      </Stack>
    );
  },
);
